"use client";

import React from 'react';
import { Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { type Article } from '@/lib/types';
import { useToast } from '@/hooks/use-toast';
import { getArticleUrl } from '@/lib/articles';

export function ShareButton({ article, className }: { article: Article, className?: string }) {
  const { toast } = useToast();

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const articlePath = getArticleUrl(article);
    const url = typeof window !== 'undefined' ? `${window.location.origin}${articlePath}` : '';
    const shareTitle = article.headline || article.title || 'News Afrika Report';
    const shareText = article.subheadline || article.lede || '';
    
    // Auto-generate caption for sharing
    const shareCaption = `Read this report on News Afrika: "${shareTitle}"\n\n${shareText}`;
    const fullShareContent = `${shareTitle}\n\n${shareText}\n\n${url}`.trim();
    
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareCaption,
          url: url,
        });
      } catch (err) {
        // User cancelled or share failed
      }
    } else if (typeof navigator !== 'undefined') {
      try {
        await navigator.clipboard.writeText(fullShareContent);
        toast({
          title: "Copied to clipboard",
          description: "Article summary and link are ready to paste.",
        });
      } catch (err) {
        toast({
          variant: "destructive",
          title: "Copy failed",
          description: "Please copy the URL from your browser address bar.",
        });
      }
    }
  };

  return (
    <Button 
      variant="ghost" 
      size="icon" 
      onClick={handleShare}
      className={className}
      aria-label="Share article"
    >
      <Share2 className="w-4 h-4" />
    </Button>
  );
}
